import { useState, useEffect } from 'react'
import { Modal, Button, Input, Select } from './UI'

interface Field {
  name: string
  label: string
  type: 'text' | 'email' | 'tel' | 'url' | 'select' | 'textarea'
  options?: { value: string; label: string }[]
  required?: boolean                                        
  validation?: (value: any) => string | undefined
}

interface CRUDModalProps<T> {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: Partial<T>) => Promise<void>
  title: string
  fields: Field[]
  initialData?: Partial<T>
  loading?: boolean
}

export function CRUDModal<T extends Record<string, any>>({
  isOpen,
  onClose,
  onSubmit,
  title,
  fields,
  initialData,
  loading
}: CRUDModalProps<T>) {
  const [formData, setFormData] = useState<Partial<T>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (initialData) {
      setFormData(initialData)
    }
  }, [initialData])

  const handleChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const newErrors: Record<string, string> = {}
    fields.forEach(field => {
      if (field.required && !formData[field.name as keyof T]) {
        newErrors[field.name] = `${field.label} is required`
      } else if (field.validation && formData[field.name as keyof T]) {
        const error = field.validation(formData[field.name as keyof T])
        if (error) newErrors[field.name] = error
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    await onSubmit(formData)
    setFormData({})
    setErrors({})
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {fields.map(field => (
          <div key={field.name}>
            {field.type === 'select' ? (
              <Select
                label={field.label}
                value={formData[field.name as keyof T] as string || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                options={field.options || []}
                error={errors[field.name]}
              />
            ) : field.type === 'textarea' ? (
              <div>
                <label className="block text-sm font-medium mb-1">{field.label}</label>
                <textarea
                  value={formData[field.name as keyof T] as string || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={4}
                />
                {errors[field.name] && (
                  <p className="text-sm text-red-600 mt-1">{errors[field.name]}</p>
                )}
              </div>
            ) : (
              <Input
                label={field.label}
                type={field.type}
                value={formData[field.name as keyof T] as string || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                error={errors[field.name]}
              />
            )}
          </div>
        ))}
        
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            {initialData ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}